import React from 'react'
import { UseFormReset } from 'react-hook-form'
import { ChargingCardToWallboxFormData } from './charging-card-to-wallbox-form-data'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import Dialog from '@e3dc-react/shell/Elements/Dialog/Dialog'
import { Typography } from '@mui/material'
import { useUnlinkTokens } from '../../api/use-unlink-tokens'
import { ChargingCardModel } from '../../api/models/charging-card-model'
import { WallboxModel } from '../../../wallboxes/models/wallbox-model'

export interface ManageModeConfirmationDialogProps {
  open: boolean
  onClose: () => void
  place: number
  reset: UseFormReset<ChargingCardToWallboxFormData>
  chargingCards: ChargingCardModel[]
  wallboxes: WallboxModel[]
  installationId: number
}

/**
 * Confirmation dialog for return to unmanaged mode for charging cards - unlink all wallboxes from all charging cards.
 */

export const ManageModeConfirmationDialog: React.FC<ManageModeConfirmationDialogProps> = ({
  open,
  onClose,
  place,
  reset,
  chargingCards,
  wallboxes,
  installationId,
}) => {
  const { t } = useCustomTranslation()

  const onSuccess = (): void => {
    /**
     * After a successful unlink of all wallboxes from all charging cards, we can reset the original form to all false
     * values.
     *
     * Again, as when initializing default values, we loop over all charging cards and then for each top-level key that
     * is the charging card id, inside we init a new Record that contains key-value pairs for each wallbox, BUT having
     * all values set to "false" since there are no more links between wallboxes and cards.
     *
     * {
     *   "chargingCardId1": {
     *     "wallboxId1": false,
     *     "wallboxId2": false,
     *     ...
     *   },
     *     "chargingCardId2": {
     *     "wallboxId1": false,
     *     ...
     *   },
     * }
     */
    reset(
      Object.fromEntries(
        chargingCards.map(chargingCard => {
          return [
            chargingCard.ocpiTokenId,
            Object.fromEntries(
              wallboxes.map(wallbox => {
                return [wallbox.wallboxId, false]
              })
            ),
          ]
        })
      )
    )
  }

  const { unlinkTokens, isLoading } = useUnlinkTokens({
    installationId,
    onClose,
    onSuccess,
  })

  return (
    <Dialog
      maxWidth="sm"
      title={t('emobility.charging-cards.manged-mode-confirmation-dialog-title')}
      onClose={onClose}
      place={place}
      actions={{
        secondary: {
          name: t('common.no'),
          disabled: isLoading,
        },
        primary: {
          name: t('common.yes'),
          onClick: () => unlinkTokens(),
          disabled: isLoading,
          isLoading,
        },
      }}
    >
      <Typography>{t('emobility.charging-cards.manged-mode-confirmation-dialog-content')}</Typography>
    </Dialog>
  )
}
